/**
 * list-helpers — antd Table 列表的共享配置與工廠函式。
 *
 * - DEFAULT_PAGINATION: 4 個 Tab 共用的分頁設定
 * - renderViewAction: 「查看」按鈕列工廠（用於 column.render）
 * - renderFilterResetExtra: FilterBar.extra 的「重置」按鈕（任一 filter 非空才顯示）
 */
import type { ReactNode } from 'react';
import { Button } from 'antd';
import { Eye } from 'lucide-react';

/**
 * 4 個 Tab 共用的 antd Table.pagination 配置。
 * pageSize=10 / 顯示頁碼切換 / 顯示「共 N 筆」。
 */
export const DEFAULT_PAGINATION = {
  pageSize: 10,
  showSizeChanger: true,
  showTotal: (t: number) => `共 ${t} 筆`,
} as const;

/**
 * 生成「查看」操作列的 render 函式。
 *
 * @param onView - 點擊行時的回呼（接收當前 record）
 * @returns column.render 簽名的函式
 */
export function renderViewAction<T>(
  onView: (record: T) => void,
): (_: unknown, record: T) => ReactNode {
  return (_: unknown, record: T) => (
    <Button type="link" size="small" icon={<Eye size={14} />} onClick={() => onView(record)}>
      查看
    </Button>
  );
}

/**
 * 生成 FilterBar.extra 用的「重置」按鈕節點。
 * 只在任一篩選非空時顯示，避免初始狀態下出現無意義按鈕。
 *
 * @param hasAnyFilter - 任一篩選欄位非空時為 true
 * @param onReset - 點擊重置時的回呼（呼叫方需自行清空各 filter state）
 * @returns 按鈕節點，或 undefined（不渲染）
 */
export function renderFilterResetExtra(
  hasAnyFilter: boolean,
  onReset: () => void,
): ReactNode | undefined {
  if (!hasAnyFilter) return undefined;
  return (
    <Button type="link" size="small" onClick={onReset}>
      重置
    </Button>
  );
}
